import type { MessageKey } from '@ecom/i18n';
import type { BadgeTone } from '@ecom/ui-kit';

import type { ConnectionState, JibexAccountView } from '@/features/delivery/jibex-account/types';
import type { DeliveryShipment } from '@/features/delivery/types';
import type { InventoryRow } from '@/features/inventory/api';

/**
 * « À traiter » — what needs the merchant's attention NOW, derived from data that already exists: orders, stock,
 * delivery and the Jibex account. Pure functions, so the rules are decided (and tested) apart from the rendering.
 *
 * Nothing is stored: every item is a count or a status read from the live data. A section whose data could not be
 * loaded says so (it is never shown as « nothing to do »), and a section with nothing to do shows no item.
 */

export type TodoSectionId = 'orders' | 'stock' | 'delivery' | 'jibex';

/** How far a piece of data has got: still loading, failed, or ready. */
export type Loadable<T> =
  | { readonly state: 'pending' }
  | { readonly state: 'error' }
  /** The API answered 403: this member's role has no access to that data (not a failure). */
  | { readonly state: 'forbidden' }
  | { readonly state: 'ready'; readonly data: T };

export interface TodoItem {
  readonly id: string;
  readonly tone: BadgeTone;
  readonly titleKey: MessageKey;
  readonly params: Readonly<Record<string, string>>;
  readonly descriptionKey: MessageKey;
  /** Extra detail line (e.g. the names of the items concerned): real data, never a placeholder. */
  readonly detail: string | null;
  readonly href: string;
}

export interface TodoSection {
  readonly id: TodoSectionId;
  readonly status: 'pending' | 'error' | 'forbidden' | 'ready';
  readonly items: readonly TodoItem[];
}

/**
 * Whether a shipment that failed to go out is something the merchant can act on. It is NOT while delivery (L3) is not
 * live: the dispatch job then fails for EVERY packed order (no carrier is wired), so the same orders would be counted
 * twice — « waiting to ship » and « failed » — with a red alert nobody can resolve. Flip this when L3 ships.
 */
export const DISPATCH_FAILURES_ACTIONABLE = false;

export interface TodoOptions {
  readonly dispatchFailuresActionable?: boolean;
}

/** Severity wording, said in words as well as in colour (a colour alone is invisible to some readers). */
export function severityKey(tone: BadgeTone): MessageKey {
  if (tone === 'critical') return 'merchant.todo.severity.urgent';
  if (tone === 'warn') return 'merchant.todo.severity.watch';
  return 'merchant.todo.severity.info';
}

export interface OrderCounts {
  /** `PAYMENT_CONFIRMED`: accepted, not yet being prepared. */
  readonly toPrepare: number;
  /** `PROCESSING`: being prepared, not yet packed. */
  readonly toPack: number;
  /** `PACKED`: packed, no parcel has left yet. */
  readonly toShip: number;
}

export interface TodoInput {
  readonly orders: Loadable<OrderCounts>;
  readonly stock: Loadable<readonly InventoryRow[]>;
  readonly shipments: Loadable<readonly DeliveryShipment[]>;
  /** `undefined` for a member who is not the owner: the Jibex account is the owner's alone, so the section is left out. */
  readonly jibex: Loadable<JibexAccountView> | undefined;
}

/** How many item names a stock alert spells out. */
export const STOCK_NAMES_SHOWN = 5;

function sectionOf(
  id: TodoSectionId,
  loadables: readonly Loadable<unknown>[],
  build: () => readonly TodoItem[],
): TodoSection {
  if (loadables.some((loadable) => loadable.state === 'forbidden')) {
    return { id, status: 'forbidden', items: [] };
  }
  if (loadables.some((loadable) => loadable.state === 'error')) {
    return { id, status: 'error', items: [] };
  }
  if (loadables.some((loadable) => loadable.state === 'pending')) {
    return { id, status: 'pending', items: [] };
  }
  return { id, status: 'ready', items: build() };
}

function ordersItems(counts: OrderCounts): TodoItem[] {
  const items: TodoItem[] = [];
  if (counts.toPrepare > 0) {
    items.push({
      id: 'orders-to-prepare',
      tone: 'info',
      titleKey: 'merchant.todo.orders.toPrepare',
      params: { count: String(counts.toPrepare) },
      descriptionKey: 'merchant.todo.orders.toPrepareDescription',
      detail: null,
      href: '/orders?status=PAYMENT_CONFIRMED',
    });
  }
  if (counts.toPack > 0) {
    items.push({
      id: 'orders-to-pack',
      tone: 'info',
      titleKey: 'merchant.todo.orders.toPack',
      params: { count: String(counts.toPack) },
      descriptionKey: 'merchant.todo.orders.toPackDescription',
      detail: null,
      href: '/orders?status=PROCESSING',
    });
  }
  return items;
}

function itemName(row: InventoryRow): string {
  return row.sku === '' ? row.productName : `${row.productName} (${row.sku})`;
}

function namesOf(rows: readonly InventoryRow[]): string | null {
  if (rows.length === 0) return null;
  const shown = rows.slice(0, STOCK_NAMES_SHOWN).map(itemName).join(', ');
  const hidden = rows.length - STOCK_NAMES_SHOWN;
  return hidden > 0 ? `${shown} +${String(hidden)}` : shown;
}

function stockItems(rows: readonly InventoryRow[]): TodoItem[] {
  const out = rows.filter((row) => row.stock.state === 'out_of_stock');
  const low = rows.filter((row) => row.stock.state === 'low_stock');
  const items: TodoItem[] = [];
  if (out.length > 0) {
    items.push({
      id: 'stock-out',
      tone: 'critical',
      titleKey: 'merchant.todo.stock.out',
      params: { count: String(out.length) },
      descriptionKey: 'merchant.todo.stock.outDescription',
      detail: namesOf(out),
      href: '/inventory',
    });
  }
  if (low.length > 0) {
    items.push({
      id: 'stock-low',
      tone: 'warn',
      titleKey: 'merchant.todo.stock.low',
      params: { count: String(low.length) },
      descriptionKey: 'merchant.todo.stock.lowDescription',
      detail: namesOf(low),
      href: '/inventory',
    });
  }
  return items;
}

/** A shipment that did not go through: failed for good, or already tried and still waiting. A first, untried one is not an alert. */
export function failedShipments(
  shipments: readonly DeliveryShipment[],
): readonly DeliveryShipment[] {
  return shipments.filter(
    (shipment) => shipment.status === 'dispatch_failed' || shipment.attemptCount > 0,
  );
}

function deliveryItems(
  counts: OrderCounts,
  shipments: readonly DeliveryShipment[],
  failuresActionable: boolean,
): TodoItem[] {
  const items: TodoItem[] = [];
  const failed = failuresActionable ? failedShipments(shipments) : [];
  if (failed.length > 0) {
    items.push({
      id: 'delivery-failed',
      tone: 'critical',
      titleKey: 'merchant.todo.delivery.failed',
      params: { count: String(failed.length) },
      descriptionKey: 'merchant.todo.delivery.failedDescription',
      detail: null,
      href: '/delivery',
    });
  }
  if (counts.toShip > 0) {
    items.push({
      id: 'delivery-to-ship',
      tone: 'warn',
      titleKey: 'merchant.todo.delivery.toShip',
      params: { count: String(counts.toShip) },
      descriptionKey: 'merchant.todo.delivery.toShipDescription',
      detail: null,
      // The ORDERS waiting (what the count is of), not /delivery, which lists shipments and can show another number.
      href: '/orders?status=PACKED',
    });
  }
  return items;
}

interface JibexEntry {
  readonly tone: BadgeTone;
  readonly title: MessageKey;
  readonly description: MessageKey;
}

const JIBEX_ITEM: Readonly<Record<Exclude<ConnectionState, 'connected'>, JibexEntry>> = {
  not_configured: {
    tone: 'warn',
    title: 'merchant.todo.jibex.not_configured',
    description: 'merchant.todo.jibex.not_configuredDescription',
  },
  untested: {
    tone: 'warn',
    title: 'merchant.todo.jibex.untested',
    description: 'merchant.todo.jibex.untestedDescription',
  },
  invalid: {
    tone: 'critical',
    title: 'merchant.todo.jibex.invalid',
    description: 'merchant.todo.jibex.invalidDescription',
  },
  misconfigured: {
    tone: 'critical',
    title: 'merchant.todo.jibex.misconfigured',
    description: 'merchant.todo.jibex.misconfiguredDescription',
  },
  network_error: {
    tone: 'warn',
    title: 'merchant.todo.jibex.network_error',
    description: 'merchant.todo.jibex.network_errorDescription',
  },
};

function jibexItems(view: JibexAccountView): TodoItem[] {
  const items: TodoItem[] = [];
  if (!view.platformReady) {
    items.push({
      id: 'jibex-platform',
      tone: 'warn',
      titleKey: 'merchant.todo.jibex.platformNotReady',
      params: {},
      descriptionKey: 'merchant.todo.jibex.platformNotReadyDescription',
      detail: null,
      href: '/delivery/account',
    });
  }
  if (view.state !== 'connected') {
    const entry = JIBEX_ITEM[view.state];
    items.push({
      id: `jibex-${view.state}`,
      tone: entry.tone,
      titleKey: entry.title,
      params: {},
      descriptionKey: entry.description,
      detail: null,
      href: '/delivery/account',
    });
  }
  return items;
}

export function buildTodo(input: TodoInput, options: TodoOptions = {}): readonly TodoSection[] {
  const { orders, stock, shipments, jibex } = input;
  const failuresActionable = options.dispatchFailuresActionable ?? DISPATCH_FAILURES_ACTIONABLE;
  const sections: TodoSection[] = [
    sectionOf('orders', [orders], () => (orders.state === 'ready' ? ordersItems(orders.data) : [])),
    sectionOf('stock', [stock], () => (stock.state === 'ready' ? stockItems(stock.data) : [])),
    sectionOf('delivery', [orders, shipments], () =>
      orders.state === 'ready' && shipments.state === 'ready'
        ? deliveryItems(orders.data, shipments.data, failuresActionable)
        : [],
    ),
  ];
  // The Jibex account only matters when there is a parcel to send: with no packed order waiting, an alert about it is
  // noise (a merchant who does not use Jibex would see it for ever). It also stays out of the way of the owner-only
  // rule (`undefined` = not the owner) and of orders that failed to load (the delivery section already says so).
  if (jibex !== undefined && orders.state !== 'error' && orders.state !== 'forbidden') {
    const nothingToShip = orders.state === 'ready' && orders.data.toShip === 0;
    if (!nothingToShip) {
      sections.push(
        sectionOf('jibex', [orders, jibex], () =>
          jibex.state === 'ready' ? jibexItems(jibex.data) : [],
        ),
      );
    }
  }
  return sections;
}

/**
 * `true` only when every section the member can see is loaded and none has anything to do — never while something is
 * loading or failed. A section the role cannot read does not count either way, but at least one must be readable:
 * « nothing to do » is never said about data nobody could look at.
 */
export function isAllClear(sections: readonly TodoSection[]): boolean {
  const visible = sections.filter((section) => section.status !== 'forbidden');
  return (
    visible.length > 0 &&
    visible.every((section) => section.status === 'ready' && section.items.length === 0)
  );
}

/**
 * A brand-new shop: loaded, no product at all and no order to handle. « Nothing to do » would be misleading there —
 * everything is still to do — so the page says how to start instead.
 */
export function isNewShop(input: Pick<TodoInput, 'orders' | 'stock'>): boolean {
  const { orders, stock } = input;
  return (
    stock.state === 'ready' &&
    stock.data.length === 0 &&
    orders.state === 'ready' &&
    orders.data.toPrepare === 0 &&
    orders.data.toPack === 0 &&
    orders.data.toShip === 0
  );
}
